import uuid from 'uuid';
import cloneDeep from 'lodash-es/cloneDeep';
import countBy from 'lodash-es/countBy';
import filter from 'lodash-es/filter';
import flattenDeep from 'lodash-es/flattenDeep';
import get from 'lodash-es/get';
import has from 'lodash-es/has';
import identity from 'lodash-es/identity';
import includes from 'lodash-es/includes';
import isArray from 'lodash-es/isArray';
import isBoolean from 'lodash-es/isBoolean';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import isFunction from 'lodash-es/isFunction';
import isNil from 'lodash-es/isNil';
import isObjectLike from 'lodash-es/isObjectLike';
import map from 'lodash-es/map';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import pickBy from 'lodash-es/pickBy';
import reverse from 'lodash-es/reverse';
import set from 'lodash-es/set';
import sortBy from 'lodash-es/sortBy';
import uniq from 'lodash-es/uniq';
import uniqWith from 'lodash-es/uniqWith';
import fabric from './fabric';
import * as p from './properties';
import utils from './utils';
import variables from './variables';
import { memoizeGetter } from './decorators';
import { scaleDownObjectToFitSize, scaleDownObjectToFillSize } from './canvas';
import { getGradientAngleFromFabricGradientCoords } from './gradients';
import { MODE_SLIDE, MODE_TEMPLATE } from './lib/modes';

// Utils

export const getObjectType = object => get(object, 'ludus.type');

export const isObjectOfType = (object, ...types) =>
  types.includes(getObjectType(object));

// Common properties

const SHADOW_PROPERTIES = [
  p.SHADOW_COLOR,
  p.SHADOW_BLUR,
  p.SHADOW_OFFSET_X,
  p.SHADOW_OFFSET_Y,
];

const COMMON_SHAPE_PERSISTED_PROPERTIES = [
  p.FILL,
  p.STROKE,
  p.STROKE_WIDTH,
  p.INNER_STROKE,
  p.INNER_STROKE_WIDTH,
  ...SHADOW_PROPERTIES,
];

// Common props

const COMMON_DIMENSIONS_PROPS = [
  [[p.PROP_ALIGN]],
  [
    [p.PROP_LEFT, p.PROP_TOP],
    [p.PROP_WIDTH, p.PROP_HEIGHT],
    // [p.PROP_SCALE_X, p.PROP_SCALE_Y],
    [p.PROP_ANGLE, p.PROP_SKEW_X],
  ],
];

const COMMON_TEXT_PROPS = [
  [
    [p.PROP_FONT_FAMILY],
    [p.PROP_TEXT_ALIGN],
    [p.PROP_FONT_STYLE],
    [p.PROP_TEXT_FILL],
    [p.PROP_FONT_SIZE],
    [p.PROP_LINE_HEIGHT],
    [p.PROP_SPACE_AFTER_PARAGRAPH],
    [p.PROP_CHAR_SPACING],
  ],
];

// Props parameters configuration

const COMMON_PROPS_CONFIG = {
  [p.PROP_FIT]: {
    label: 'Fit to canvas',
    name: 'fit',
    target: 'global',
    action: 'fitToCanvas',
    helpText: `Enlarge object to cover the whole slide (${
      utils.isMac ? '⌘E' : 'Ctrl+E'
    })`,
  },
};

const COMMON_READONLY_SIZE_PROPS_CONFIG = {
  [p.PROP_HEIGHT]: {
    readOnly: true,
  },
  [p.PROP_WIDTH]: {
    readOnly: true,
  },
};

const COMMON_EFFECTS_PROPS_CONFIG = {
  [p.PROP_BLUR]: {
    title: 'Blur',
    name: p.BLUR,
    min: 0,
    max: 50,
    debounceDelay: 25,
    inTransformation: value => value * 100,
    outTransformation: value => value / 100,
  },
  [p.PROP_BRIGHTNESS]: {
    title: 'Brightness',
    name: p.BRIGHTNESS,
    debounceDelay: 25,
    help: 'Adjust the brightness',
    min: -100,
    max: 100,
  },
  [p.PROP_CONTRAST]: {
    title: 'Contrast',
    help: 'Blacks can be darker',
    debounceDelay: 25,
    name: p.CONTRAST,
    min: -100,
    max: 100,
  },
  [p.PROP_SATURATION]: {
    title: 'Saturation',
    debounceDelay: 25,
    help: 'From black and white to super colorized',
    name: p.SATURATION,
    min: -100,
    max: 200,
  },
  [p.PROP_INVERT_COLORS]: {
    title: 'Invert colors',
    name: p.INVERT,
    help: 'Invert the colors of the image',
  },
};

// Active selection configuration

const ACTIVE_SELECTION = {
  name: 'Selection',
  // Exhaustive props layout for active selection, some props will be omitted
  // if not editable in all objects within selection
  propsLayout: [
    [
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        [p.PROP_ANGLE, p.PROP_SCALE, p.PROP_LENGTH],
      ],

      [[p.PROP_ARRANGE, p.PROP_FLIP, p.PROP_GROUP, p.PROP_LOCK]],

      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      ...COMMON_TEXT_PROPS,
      [[p.PROP_FILL], [p.PROP_STROKE], [p.PROP_BORDER_RADIUS]],
      [[p.PROP_BRUSH_SIZE]],
      [[p.PROP_SHADOW], [p.PROP_TINT], [p.PROP_INVERT_COLORS]],
      [[p.PROP_ARROW_1], [p.PROP_ARROW_2], [p.PROP_ARROWSIZE]],
      [
        [p.PROP_CONTRAST],
        [p.PROP_BRIGHTNESS],
        [p.PROP_SATURATION],
        [p.PROP_BLUR],
      ],
      [
        [p.PROP_SPECIAL_AUTOPLAY],
        [p.PROP_SPECIAL_CONTROLS],
        [p.PROP_SPECIAL_LOOP],
        [p.PROP_SPECIAL_MUTE],
        [p.PROP_SPECIAL_REVERSE],
      ],
    ],
  ],
  // Force displaying some ActiveSelection-specific props
  mandatoryProps: [
    p.PROP_ALIGN,
    p.PROP_LEFT,
    p.PROP_TOP,
    p.PROP_WIDTH,
    p.PROP_HEIGHT,
  ],
};

// Objects configuration

const ARROW = {
  name: 'Line',
  type: 'arrow',
  fabricClass: 'LudusLine',
  defaultProperties: {
    arrow2: true,
    fill: variables.defaultProperties.fill,
    stroke: variables.defaultProperties.fill,
    strokeWidth: 10,
    strokeDashArray: null,
    shadow: variables.shadows.default,
    arrowSize: 40,
    width: 480,
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: [
    ...SHADOW_PROPERTIES,
    p.STROKE,
    p.STROKE_WIDTH,
    p.INNER_STROKE,
    p.INNER_STROKE_WIDTH,
    p.ARROWSIZE,
  ],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_ALIGN]],
      [[p.PROP_POINT_1], [p.PROP_POINT_2], [p.PROP_ANGLE, p.PROP_LENGTH]],

      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_STROKE]],
      [
        [p.PROP_ARROW_1],
        [p.PROP_ARROW_2],
        (object.arrow1 || object.arrow2) && [p.PROP_ARROWSIZE],
      ],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    [p.PROP_POINT_1]: {
      xTitle: 'X₁',
      yTitle: 'Y₁',
      xName: p.X1,
      yName: p.Y1,
    },
    [p.PROP_POINT_2]: {
      xTitle: 'X₂',
      yTitle: 'Y₂',
      xName: p.X2,
      yName: p.Y2,
    },
    [p.PROP_ARROW_1]: {
      title: 'Start Arrow',
      name: p.ARROW_1,
    },
    [p.PROP_ARROW_2]: {
      title: 'End Arrow',
      name: p.ARROW_2,
    },
    [p.PROP_ARROWSIZE]: {
      min: fabric.LudusLine.MIN_THICKNESS,
      max: fabric.LudusLine.MAX_THICKNESS,
    },
  },
};

const BRUSH_PATH = {
  name: 'Brush path',
  type: 'brushPath',
  fabricClass: 'LudusBrushPath',
  defaultProperties: {
    fill: variables.defaultProperties.fill,
    originX: 'left',
    originY: 'top',
    shadow: variables.shadows.default,
  },
  toggleablePersistedProperties: [p.FILL, p.SHADOW],
  persistedProperties: [...COMMON_SHAPE_PERSISTED_PROPERTIES],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_BRUSH_SIZE]],
      [[p.PROP_FILL]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,

    [p.PROP_BRUSH_SIZE]: {
      title: 'Brush Size',
      name: 'size',
      min: 1,
      max: 100,
      step: 1,
      precision: 1,
    },
  },
};

const CIRCLE = {
  name: 'Circle',
  type: 'circle',
  fabricClass: 'LudusCircle',
  defaultProperties: {
    fill: variables.defaultProperties.fill,
    stroke: 'rgba(0,0,0,1)',
    strokeWidth: 3,
    originX: 'left',
    originY: 'top',
    rx: 100,
    ry: 100,
    shadow: variables.shadows.default,
  },
  toggleablePersistedProperties: [p.STROKE, p.SHADOW],
  persistedProperties: [...COMMON_SHAPE_PERSISTED_PROPERTIES],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_FILL], [p.PROP_STROKE]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
  },
};

const GROUP = {
  name: 'Group',
  type: 'group',
  defaultProperties: {},
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        [p.PROP_ANGLE, p.PROP_SCALE],
      ],
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    //  ...COMMON_READONLY_SIZE_PROPS_CONFIG,
  },
};

const CODE = {
  name: 'Code',
  type: 'code',
  fabricClass: 'LudusCode',
  configFunc: (object, canvasSize) => {
    scaleDownObjectToFitSize(object, canvasSize);
  },
  defaultProperties: {
    originX: 'left',
    originY: 'top',
    lockUniScaling: true,
    shadow: variables.shadows.default,
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: [],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_CODE_INFO]],
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        [p.PROP_ANGLE, p.PROP_SCALE],
      ],
      [[p.PROP_ARRANGE, p.PROP_FIT, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY]],
      [[p.PROP_SHADOW]],
    ],
  ],
  propsConfig: object => ({
    ...COMMON_PROPS_CONFIG,
    [p.PROP_HEIGHT]: {
      readOnly: object.lockUniScaling,
    },
    [p.PROP_WIDTH]: {
      readOnly: object.lockUniScaling,
    },
  }),
};

const IFRAME = {
  name: 'Iframe',
  type: 'iframe',
  fabricClass: 'LudusIframe',
  defaultProperties: {
    ludus: {
      applyScale: false,
    },
    originX: 'left',
    originY: 'top',
    shadow: variables.shadows.default,
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: [...SHADOW_PROPERTIES],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_ARRANGE, p.PROP_FIT, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_URL]],
      // [[p.PROP_SPECIAL_ADAPTATIVE_SCALE]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    // [p.PROP_SPECIAL_ADAPTATIVE_SCALE]: {
    //   title: 'Adaptative scale',
    //   name: p.SPECIAL_APPLY_SCALE,
    // },
  },
};

const GUIDELINE = {
  name: 'Guideline',
  type: 'guideline',
  fabricClass: 'LudusGuideline',
  defaultProperties: {
    ludus: {
      applyScale: true,
    },
    originX: 'left',
    originY: 'top',
  },
  propsLayout: (object, mode) => [
    [[[p.PROP_LEFT, p.PROP_TOP]], [[p.PROP_FILL]]],
  ],
  propsConfig: object => ({
    ...COMMON_PROPS_CONFIG,
    [p.PROP_TOP]: {
      readOnly:
        object.type === 'ludusGuideline' && object.direction === 'vertical',
    },
    [p.PROP_LEFT]: {
      readOnly:
        object.type === 'ludusGuideline' && object.direction === 'horizontal',
    },
  }),
};

const CHART = {
  name: 'Chart',
  type: 'chart',
  fabricClass: 'LudusChart',
  defaultProperties: {
    ludus: {
      applyScale: true,
    },
    originX: 'left',
    originY: 'top',
  },
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_SCALE]],
      [[p.PROP_ARRANGE, p.PROP_FIT, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY]],
      [[p.PROP_CHART_INFO]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
  },
};

const IMAGE = {
  name: object => {
    if (object.isCropping()) {
      return 'Image (crop)';
    }
    return 'Image';
  },
  type: 'image',
  fabricClass: 'LudusImage',
  configFunc: (object, canvasSize) => {
    const objectRatio = object.width / object.height;
    const canvasRatio = canvasSize.width / canvasSize.height;
    const sameOrientation =
      (objectRatio < 1 && canvasRatio < 1) ||
      (objectRatio > 1 && canvasRatio > 1);
    const objectBigSide = objectRatio > 1 ? object.width : object.height;
    const canvasBigSide =
      canvasRatio > 1 ? canvasSize.width : canvasSize.height;
    if (sameOrientation && objectBigSide >= canvasBigSide) {
      // Try to cover canvas
      scaleDownObjectToFillSize(object, canvasSize);
    } else {
      // Try to fit canvas
      scaleDownObjectToFitSize(object, canvasSize);
    }
  },
  canBeAddedToSymbol: object => !object.isCropping(),
  defaultProperties: {
    originX: 'left',
    originY: 'top',
    shadow: variables.shadows.default,
    tint: variables.defaultProperties.translucentTint,
    saturation: 0,
    contrast: 0,
    brightness: 0,
    invert: false,
    hue: 0,
    blur: 0,
    imageCropX: 0,
    imageCropY: 0,
    ludus: {
      ludusImageVersion: '2.0',
      highQuality: false,
    },
  },
  toggleablePersistedProperties: [p.SHADOW, p.TINT],
  persistedProperties: [...SHADOW_PROPERTIES, p.TINT],
  propsLayout: (object, mode) => {
    if (object.isCropping()) {
      return [
        [
          [
            // [p.PROP_CROP_POINT],
            [p.PROP_WIDTH, p.PROP_HEIGHT],
          ],
          [[p.PROP_ACTION_RESET]],
        ],
        [[[p.PROP_ACTION_CANCEL, p.PROP_SPACER, p.PROP_ACTION_APPLY]]],
      ];
    }
    return [
      [
        mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
        [[p.PROP_ALIGN]],
        [
          [p.PROP_LEFT, p.PROP_TOP],
          [p.PROP_WIDTH, p.PROP_HEIGHT],
          [p.PROP_ANGLE, p.PROP_SKEW_X],
        ],
        [[p.PROP_ARRANGE, p.PROP_FLIP, p.PROP_FIT, p.PROP_LOCK, p.PROP_GROUP]],
        [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
        [
          [p.PROP_IMAGE_SOURCE],
          object.isEditable() ? [[p.PROP_ACTION_CROP]] : null,
          [[p.PROP_RICH_INFO]],
        ],
        [[p.PROP_BORDER_RADIUS]],
        [[p.PROP_SPECIAL_HIGH_QUALITY]],

        object.canApplyFilters()
          ? [
              [p.PROP_SHADOW],
              [p.PROP_TINT],
              [p.PROP_CONTRAST],
              [p.PROP_BRIGHTNESS],
              [p.PROP_SATURATION],
              object.ludus.format === 'svg' ? [] : [p.PROP_BLUR],
              [p.PROP_INVERT_COLORS],
            ]
          : null,
        [[p.PROP_SPECIAL_HYPERLINK]],
      ],
    ];
  },
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    ...COMMON_EFFECTS_PROPS_CONFIG,
    [p.PROP_BORDER_RADIUS]: {
      min: 0,
      max: 200,
    },
    // [p.PROP_CROP_POINT]: {
    //   xTitle: 'X',
    //   yTitle: 'Y',
    //   xName: p.CROP_X,
    //   yName: p.CROP_Y,
    // },
    [p.PROP_ACTION_CROP]: {
      label: 'Crop',
      name: 'crop',
      target: 'object',
      action: 'enterCroppingMode',
      disabled: object => object.ludus.locked || object.ludus.format === 'svg',
    },
    [p.PROP_ACTION_RESET]: {
      label: 'Reset dimensions',
      name: 'reset',
      target: 'object',
      action: 'resetCrop',
      disabled: object => {
        if (object && isFunction(object.hasCropChanges)) {
          return !object.hasCropChanges();
        }
        return true;
      },
    },
    [p.PROP_ACTION_CANCEL]: {
      label: 'Cancel',
      name: 'cancel',
      target: 'object',
      action: 'cancelCroppingMode',
      expand: false,
    },
    [p.PROP_ACTION_APPLY]: {
      label: 'Apply changes',
      name: 'apply',
      target: 'object',
      action: 'applyCroppingMode',
      disabled: object => {
        if (object && isFunction(object.hasNewChanges)) {
          return !object.hasNewChanges();
        }
        return true;
      },
      primary: true,
    },
    [p.PROP_SPECIAL_HIGH_QUALITY]: {
      title: 'High quality',
      name: p.SPECIAL_HIGH_QUALITY,
    },
  },
};

const LINE = cloneDeep(ARROW);
LINE.type = 'line';
LINE.defaultProperties = {
  ...ARROW.defaultProperties,
  arrow2: false,
  width: 640,
};

const LOTTIE = {
  name: 'Lottie animation',
  type: 'lottie',
  fabricClass: 'LudusLottie',
  defaultProperties: {
    originX: 'left',
    originY: 'top',
    lockUniScaling: true,
    shadow: variables.shadows.default,
    ludus: {
      source: 'lottie',
      loop: true,
      reverse: false,
      speed: 1,
      delay: 0,
    },
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: SHADOW_PROPERTIES,
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        [p.PROP_ANGLE, p.PROP_SCALE],
      ],
      [[p.PROP_ARRANGE, p.PROP_FLIP, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
      [
        [p.PROP_SPECIAL_LOOP],
        [p.PROP_SPECIAL_REVERSE],
        [p.PROP_SPECIAL_SPEED],
        [p.PROP_SPECIAL_DELAY],
      ],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    ...COMMON_READONLY_SIZE_PROPS_CONFIG,
    [p.PROP_SPECIAL_LOOP]: {
      title: 'Loop',
      name: p.SPECIAL_LOOP,
    },
    [p.PROP_SPECIAL_REVERSE]: {
      title: 'Reverse',
      name: p.SPECIAL_REVERSE,
    },
    [p.PROP_SPECIAL_SPEED]: {
      title: 'Speed',
      name: p.SPECIAL_SPEED,
      min: 0.25,
      max: 4,
      step: 0.1,
      precision: 2,
    },
    [p.PROP_SPECIAL_DELAY]: {
      title: 'Delay',
      name: p.SPECIAL_DELAY,
      defaultValue: 1,
      min: 0,
      max: 10,
      step: 0.1,
      precision: 1,
      help: 'The animation will play after the specified delay (in seconds)',
    },
  },
};

const PARAGRAPH = {
  name: 'Text',
  type: 'paragraph',
  fabricClass: 'LudusText',
  defaultProperties: {
    editable: true,
    fontFamily: 'Roboto',
    fontSize: 32,
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: 1.2,
    originX: 'left',
    originY: 'top',
    shadow: variables.shadows.default,
    textAlign: 'left',
    width: 640,
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: [
    p.CHAR_SPACING,
    p.FILL,
    p.FONT_FAMILY,
    p.FONT_SIZE,
    p.FONT_WEIGHT,
    p.FONT_STYLE,
    p.LINE_HEIGHT,
    p.TEXT_ALIGN,
    ...SHADOW_PROPERTIES,
  ],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        [p.PROP_ANGLE, p.PROP_SKEW_X],
      ],
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      ...COMMON_TEXT_PROPS,
      [[p.PROP_LIST]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
  },
};

const PATH = {
  name: 'Path',
  type: 'path',
  fabricClass: 'Path',
  defaultProperties: {
    fill: variables.defaultProperties.fill,
    stroke: 'rgba(0,0,0,1)',
    strokeWidth: 3,
    originX: 'left',
    originY: 'top',
    shadow: variables.shadows.default,
  },
  toggleablePersistedProperties: [p.STROKE, p.SHADOW],
  persistedProperties: [...COMMON_SHAPE_PERSISTED_PROPERTIES],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_FILL], [p.PROP_STROKE]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    // ...COMMON_READONLY_SIZE_PROPS_CONFIG,
  },
};

const PATH_GROUP = {
  name: 'Graphic',
  type: 'path-group',
  fabricClass: 'LudusPathGroup',
  configFunc: object => {
    if (object.width < 300 || object.height < 300) {
      object.width > object.height
        ? object.scaleToWidth(300)
        : object.scaleToHeight(300);
    } else if (object.width > 1000 || object.height > 1000) {
      object.width > object.height
        ? object.scaleToWidth(1000)
        : object.scaleToHeight(1000);
    }
  },
  defaultProperties: {
    originX: 'left',
    originY: 'top',
    lockUniScaling: true,
    shadow: variables.shadows.default,
    tint: variables.defaultProperties.opaqueTint,
  },
  toggleablePersistedProperties: [p.SHADOW, p.TINT],
  persistedProperties: [...SHADOW_PROPERTIES, p.TINT],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_SHADOW], [p.PROP_TINT]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    // ...COMMON_READONLY_SIZE_PROPS_CONFIG,
  },
};

const RECTANGLE = {
  name: 'Rectangle',
  type: 'rectangle',
  fabricClass: 'LudusRect',
  defaultProperties: {
    fill: variables.defaultProperties.fill,
    height: 200,
    stroke: 'rgba(0,0,0,1)',
    strokeWidth: 3,
    originX: 'left',
    originY: 'top',
    shadow: variables.shadows.default,
    width: 200,
  },
  toggleablePersistedProperties: [p.STROKE, p.SHADOW],
  persistedProperties: [
    ...COMMON_SHAPE_PERSISTED_PROPERTIES,
    p.BORDER_RADIUS_X,
    p.BORDER_RADIUS_Y,
  ],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      ...COMMON_DIMENSIONS_PROPS,
      [[p.PROP_ARRANGE, p.PROP_FIT, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
      [[p.PROP_FILL], [p.PROP_STROKE], [p.PROP_BORDER_RADIUS]],
      [[p.PROP_SHADOW]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    [p.PROP_BORDER_RADIUS]: {
      min: 0,
      max: 200,
    },
  },
};

const RICH = {
  name: object => utils.getLabel(get(object, 'ludus.source', 'embed')),
  type: 'rich',
  fabricClass: 'LudusRich',
  configFunc: (object, canvasSize) => {
    scaleDownObjectToFitSize(object, canvasSize);
  },
  defaultProperties: {
    originX: 'left',
    originY: 'top',
    lockUniScaling: false,
    shadow: variables.shadows.default,
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: [...SHADOW_PROPERTIES],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        object.lockUniScaling
          ? [p.PROP_ANGLE, p.PROP_SCALE]
          : [p.PROP_ANGLE, p.PROP_SKEW_X],
      ],
      [[p.PROP_ARRANGE, p.PROP_FIT, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY]],
      // Old-style rich blocks are of type 'image', and lack the data to display
      // the rich info box
      isObjectOfType(object, RICH.type) ? [[p.PROP_RICH_INFO]] : null,
      [[p.PROP_SHADOW]],
      get(object, 'ludus.source') === 'sketchfab'
        ? [
            [p.PROP_SPECIAL_AUTOPLAY],
            [p.PROP_SPECIAL_CAMERA],
            [p.PROP_SPECIAL_AUTOSPIN],
            [p.PROP_SPECIAL_TRANSPARENT],
          ]
        : null,
      get(object, 'ludus.source') === 'typeform'
        ? [
            [[p.PROP_SPECIAL_HIDE_HEADERS]],
            [[p.PROP_SPECIAL_HIDE_FOOTER]],
            [[p.PROP_SPECIAL_BACKGROUND_OPACITY]],
          ]
        : null,
      get(object, 'ludus.source') === 'codepen'
        ? [
            [p.PROP_SPECIAL_DEFAULT_TAB],

            [[p.PROP_SPECIAL_CONTROLS]],
            // [[p.PROP_SPECIAL_ADAPTATIVE_SCALE]],
            [[p.PROP_SPECIAL_THEME]],
          ]
        : null,
      get(object, 'ludus.source') === 'maps'
        ? [[p.PROP_SPECIAL_MAP_TYPE], [p.PROP_SPECIAL_ZOOM]]
        : null,
    ],
  ],
  propsConfig: object => ({
    ...COMMON_PROPS_CONFIG,
    [p.PROP_HEIGHT]: {
      readOnly: object.lockUniScaling,
    },
    [p.PROP_WIDTH]: {
      readOnly: object.lockUniScaling,
    },
    [p.PROP_SPECIAL_HIDE_HEADERS]: {
      title: 'Hide headers',
      name: p.SPECIAL_HIDE_HEADERS,
    },
    [p.PROP_SPECIAL_HIDE_FOOTER]: {
      title: 'Hide footer',
      name: p.SPECIAL_HIDE_FOOTER,
    },
    [p.PROP_SPECIAL_BACKGROUND_OPACITY]: {
      title: 'Background opacity',
      name: p.SPECIAL_BACKGROUND_OPACITY,
      defaultValue: 100,
      min: 0,
      max: 100,
      step: 1,
      precision: 0,
    },
    [p.PROP_SPECIAL_AUTOPLAY]: {
      title: 'Autoplay',
      name: p.SPECIAL_AUTOPLAY,
    },
    [p.PROP_SPECIAL_CAMERA]: {
      title: 'Animated entrance',
      name: p.SPECIAL_CAMERA,
    },
    [p.PROP_SPECIAL_AUTOSPIN]: {
      title: 'Turntable animation',
      name: p.SPECIAL_AUTOSPIN,
    },
    [p.PROP_SPECIAL_TRANSPARENT]: {
      title: 'Transparent background',
      name: p.SPECIAL_TRANSPARENT,
    },
    [p.PROP_SPECIAL_CONTROLS]: {
      title: 'Controls',
      name: p.SPECIAL_CONTROLS,
    },
    [p.PROP_SPECIAL_THEME]: {
      title: 'Theme',
      name: p.SPECIAL_THEME,
    },
    [p.PROP_SPECIAL_DEFAULT_TAB]: {
      title: 'Default tab',
      name: p.SPECIAL_DEFAULT_TAB,
    },
    // [p.PROP_SPECIAL_ADAPTATIVE_SCALE]: {
    //   title: 'Adaptative scale',
    //   name: p.SPECIAL_APPLY_SCALE,
    // },
    [p.PROP_SPECIAL_MAP_TYPE]: {
      title: 'Map type',
      name: p.SPECIAL_MAP_TYPE,
    },
    [p.PROP_SPECIAL_ZOOM]: {
      title: 'Zoom',
      name: p.SPECIAL_ZOOM,
      min: 0,
      max: 21,
    },
  }),
};

const SYMBOL = {
  name: object => object.name,
  type: 'symbol',
  fabricClass: 'LudusSymbol',
  defaultProperties: {
    originX: 'left',
    originY: 'top',
    lockUniScaling: true,
  },
  persistedProperties: [],
  propsLayout: (object, mode) => [
    [
      mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
      [[p.PROP_ALIGN]],
      [
        [p.PROP_LEFT, p.PROP_TOP],
        [p.PROP_WIDTH, p.PROP_HEIGHT],
        [p.PROP_ANGLE, p.PROP_SCALE],
      ],
      [[p.PROP_ARRANGE, p.PROP_GROUP, p.PROP_LOCK]],
      [[p.PROP_OPACITY]],
      [[p.PROP_SPECIAL_HYPERLINK]],
    ],
  ],
  propsConfig: {
    ...COMMON_PROPS_CONFIG,
    // ...COMMON_READONLY_SIZE_PROPS_CONFIG,
  },
};

const TITLE = cloneDeep(PARAGRAPH);
TITLE.type = 'title';
TITLE.defaultProperties = {
  ...PARAGRAPH.defaultProperties,
  fontSize: 70,
  fontWeight: '500',
  lineHeight: 1.0,
  textAlign: 'center',
  shadow: variables.shadows.default,
  width: 800,
};

const VIDEO = {
  name: 'Video',
  type: 'video',
  fabricClass: 'LudusImage',
  configFunc: (object, canvasSize) => {
    scaleDownObjectToFitSize(object, canvasSize);
  },
  defaultProperties: {
    crossOrigin: 'Anonymous',
    originX: 'left',
    originY: 'top',
    lockUniScaling: true,
    ludus: {
      autoplay: false,
      controls: true,
      loop: false,
      mute: false,
      highQuality: false,
    },
    shadow: variables.shadows.default,
    saturation: 0,
    contrast: 0,
    brightness: 0,
    invert: false,
    imageCropX: 0,
    imageCropY: 0,
  },
  toggleablePersistedProperties: [p.SHADOW],
  persistedProperties: [
    ...SHADOW_PROPERTIES,
    p.SPECIAL_AUTOPLAY,
    p.SPECIAL_CONTROLS,
    p.SPECIAL_LOOP,
    p.SPECIAL_MUTE,
  ],
  canBeAddedToSymbol: object => !object.isCropping(),
  propsLayout: (object, mode) => {
    if (object.isCropping()) {
      return [
        [[[p.PROP_WIDTH, p.PROP_HEIGHT]], [[p.PROP_ACTION_RESET]]],
        [[[p.PROP_ACTION_CANCEL, p.PROP_SPACER, p.PROP_ACTION_APPLY]]],
      ];
    }
    return [
      [
        mode === MODE_TEMPLATE ? [[p.PROP_SPECIAL_TEMPLATE_REFERENCE]] : null,
        [[p.PROP_ALIGN]],
        [
          [p.PROP_LEFT, p.PROP_TOP],
          [p.PROP_WIDTH, p.PROP_HEIGHT],
          [p.PROP_ANGLE, p.PROP_SCALE],
          [p.PROP_SKEW_X, p.PROP_PLACEHOLDER],
        ],
        [[p.PROP_ARRANGE, p.PROP_FLIP, p.PROP_FIT, p.PROP_GROUP, p.PROP_LOCK]],
        [[p.PROP_OPACITY], [p.PROP_BLENDING_MODE]],
        [[p.PROP_BORDER_RADIUS]],
        [[p.PROP_RICH_INFO]],
        [[p.PROP_SPECIAL_HIGH_QUALITY]],
        [
          [p.PROP_SPECIAL_AUTOPLAY],
          [p.PROP_SPECIAL_CONTROLS],
          [p.PROP_SPECIAL_LOOP],
          [p.PROP_SPECIAL_MUTE],
        ],
        get(object, 'ludus.source') === 'youtube' ||
        get(object, 'ludus.source') === 'Cloudinary'
          ? [[p.PROP_SPECIAL_START_TIME], [p.PROP_SPECIAL_END_TIME]]
          : null,
        [
          [p.PROP_SHADOW],
          [p.PROP_CONTRAST],
          [p.PROP_BRIGHTNESS],
          [p.PROP_SATURATION],
          [p.PROP_INVERT_COLORS],
        ],
      ],
    ];
  },
  propsConfig: object => ({
    ...COMMON_PROPS_CONFIG,
    ...COMMON_EFFECTS_PROPS_CONFIG,
    [p.PROP_ACTION_CROP]: {
      label: 'Crop',
      name: 'crop',
      target: 'object',
      action: 'enterCroppingMode',
      disabled: object => object.ludus.locked || object.ludus.format === 'svg',
    },
    [p.PROP_ACTION_RESET]: {
      label: 'Reset dimensions',
      name: 'reset',
      target: 'object',
      action: 'resetCrop',
      disabled: object => {
        if (object && isFunction(object.hasCropChanges)) {
          return !object.hasCropChanges();
        }
        return true;
      },
    },
    [p.PROP_ACTION_CANCEL]: {
      label: 'Cancel',
      name: 'cancel',
      target: 'object',
      action: 'cancelCroppingMode',
      expand: false,
    },
    [p.PROP_ACTION_APPLY]: {
      label: 'Apply changes',
      name: 'apply',
      target: 'object',
      action: 'applyCroppingMode',
      disabled: object => {
        if (object && isFunction(object.hasNewChanges)) {
          return !object.hasNewChanges();
        }
        return true;
      },
      primary: true,
    },
    [p.PROP_BORDER_RADIUS]: {
      min: 0,
      max: 200,
    },
    [p.PROP_SPECIAL_AUTOPLAY]: {
      title: 'Autoplay',
      help:
        'Automatically play the video (video could be muted to force autoplay)',
      name: p.SPECIAL_AUTOPLAY,
    },
    [p.PROP_SPECIAL_CONTROLS]: {
      title: 'Controls',
      help: 'Show the controls of the video',
      name: p.SPECIAL_CONTROLS,
    },
    [p.PROP_SPECIAL_LOOP]: {
      title: 'Loop',
      name: p.SPECIAL_LOOP,
      help: 'Loop the video',
    },
    [p.PROP_SPECIAL_MUTE]: {
      title: 'Mute',
      name: p.SPECIAL_MUTE,
      help: 'Mute the video',
    },
    [p.PROP_SPECIAL_START_TIME]: {
      title: 'Start Time',
      name: p.SPECIAL_START_TIME,
      help: 'The start time of the video (in seconds)',
      defaultValue: 5,
      precision: get(object, 'ludus.source') === 'Cloudinary' ? 2 : 0,
    },
    [p.PROP_SPECIAL_END_TIME]: {
      title: 'End Time',
      help: 'The end time of the video (in seconds)',
      name: p.SPECIAL_END_TIME,
      defaultValue: 30,
      precision: get(object, 'ludus.source') === 'Cloudinary' ? 2 : 0,
    },
    [p.PROP_SPECIAL_HIGH_QUALITY]: {
      title: 'High quality',
      help: 'Encode the video in extra high quality',
      name: p.SPECIAL_HIGH_QUALITY,
    },
  }),
};

// Legacy objects configuration

const TEXT = cloneDeep(PARAGRAPH);
TEXT.type = 'text';

const ALL_OBJECTS = [
  ARROW,
  CIRCLE,
  GROUP,
  CODE,
  IFRAME,
  IMAGE,
  LINE,
  PARAGRAPH,
  BRUSH_PATH,
  PATH,
  PATH_GROUP,
  RECTANGLE,
  RICH,
  SYMBOL,
  TEXT,
  TITLE,
  VIDEO,
  LOTTIE,
  CHART,
  GUIDELINE,
];

const PERSISTED_OBJECTS = [
  ARROW,
  CIRCLE,
  CODE,
  IFRAME,
  IMAGE,
  LINE,
  PARAGRAPH,
  BRUSH_PATH,
  PATH,
  PATH_GROUP,
  RECTANGLE,
  RICH,
  SYMBOL,
  TITLE,
  VIDEO,
  LOTTIE,
  CHART,
];

export const DEFAULT_OBJECTS_PROPERTIES = Object.assign(
  {},
  ...ALL_OBJECTS.map(object => ({
    [object.type]: {
      ...object.defaultProperties,
      ludus: {
        ...object.defaultProperties.ludus,
        type: object.type,
      },
    },
  })),
);

export const DEFAULT_PERSISTED_PROPERTIES = Object.assign(
  {},
  ...PERSISTED_OBJECTS.map(object => ({
    [object.type]: {
      ...object.defaultProperties,
      ludus: {
        ...object.defaultProperties.ludus,
        type: object.type,
      },
    },
  })),
);

// Some values are special Fabric objects (e.g. fabric.Shadow, fabric.Gradient).
// We need to convert them to plain objects and clean them,
// so they become actually comparable between objects
// and can be modified within an ActiveSelection.
const PROPERTY_TRANSFORMS = {
  [p.SHADOW]: value => {
    if (isObjectLike(value) && isFunction(value.toObject)) {
      return value.toObject();
    }
    return value;
  },
  [p.FILL]: value => {
    // We compute angle here and remove object-specific coords
    if (isObjectLike(value) && value.coords) {
      let newValue = value;
      if (isFunction(newValue.toObject)) {
        newValue = newValue.toObject();
      }
      return {
        ...omit(newValue, ['coords', 'gradientTransform']),
        angle: getGradientAngleFromFabricGradientCoords(newValue.coords),
      };
    }
    return value;
  },
};

const TOGGLEABLE_PROPERTY_FLAGS_DEFAULTS = {
  [p.INNER_STROKE]: true,
  [p.STROKE]: true,
  [p.SHADOW]: false,
  [p.TINT]: false,
};

// Utils

export const isActiveSelection = object =>
  object && object.type === 'activeSelection';

export const isContainer = object =>
  (isActiveSelection(object) || isObjectOfType(object, GROUP.type)) &&
  isFunction(object.getObjects);

export const isDataContainer = object =>
  (isActiveSelection(object) || isObjectOfType(object, GROUP.type)) &&
  isArray(object.objects);

export const objectContainsType = (object, type) => {
  if (isObjectOfType(object, type)) {
    return true;
  }
  if (isContainer(object)) {
    return object
      .getObjects()
      .map(o => objectContainsType(o, type))
      .includes(true);
  }
  return false;
};

export const filterObjectsByType = (objects, type) =>
  objects.filter(object => getObjectType(object) === type);

export const getObjectName = object => {
  const type = getObjectType(object);
  const typeDefinition = ALL_OBJECTS.find(def => def.type === type);
  if (typeDefinition) {
    let name;
    if (isFunction(typeDefinition.name)) {
      name = typeDefinition.name(object);
    } else {
      name = typeDefinition.name;
    }
    if (object.ludus?.format && object.ludus?.format !== 'image') {
      name = `${name} (${object.ludus.format})`;
    }
    const reference = get(object, 'ludus.reference');
    return reference ? `${name} (${reference})` : name;
  }

  return 'Object';
};

export const identifyDataObject = (object, forceId = true) => {
  if (forceId || !object.canvasId) {
    set(object, 'canvasId', uuid.v4());
  }
};

export const identifyDataObjectTree = object => {
  if (isDataContainer(object)) {
    object.objects.map(subObject =>
      identifyDataObjectTree(identifyDataObject(subObject)),
    );
  }
  return object;
};

export const applyOnObjectTree = (object, func) => {
  func(object);
  if (isContainer(object)) {
    object.forEachObject(func);
  }
  return object;
};

export const identifyObject = object => set(object, 'canvasId', uuid.v4());

export const identifyObjectTree = object =>
  applyOnObjectTree(object, identifyObject);

export const getToggleableFlagByProperty = property =>
  TOGGLEABLE_PROPERTY_FLAGS_DEFAULTS[property];

export const getTransformByProperty = property =>
  PROPERTY_TRANSFORMS[property] || identity;

export const transformProperty = (key, value) =>
  getTransformByProperty(key)(value);

export const shouldIgnoreValue = value =>
  [null, undefined, 'transparent', '', 0].includes(value);

export const getDefaultPropertiesForType = type =>
  DEFAULT_OBJECTS_PROPERTIES[type];

export const getDefaultPropertyForType = (
  type,
  property,
  defaultProperties,
) => {
  const defaultPropertiesForType = defaultProperties[type];
  if (defaultPropertiesForType) {
    const value = get(defaultPropertiesForType.properties, property);
    return transformProperty(property, value);
  }
  return variables.defaultProperties[property];
};

export const getDefaultProperty = (object, property, defaultProperties) => {
  const type = getObjectType(object);
  return getDefaultPropertyForType(type, property, defaultProperties);
};

export const getFabricClassNameForType = type => {
  const typeDefinition = ALL_OBJECTS.find(def => def.type === type);
  return typeDefinition ? typeDefinition.fabricClass : null;
};

export const getObjectPersistedProperties = object => {
  const type = getObjectType(object);
  if (type) {
    const typeDefinition = PERSISTED_OBJECTS.find(def => def.type === type);
    if (typeDefinition) {
      const properties = pick(object, typeDefinition.persistedProperties || []);
      return cloneDeep(pickBy(properties, v => !shouldIgnoreValue(v)));
    }
  }
  return {};
};

export const getPersistedPropertiesByType = type => {
  const typeDefinition = PERSISTED_OBJECTS.find(def => def.type === type);
  if (typeDefinition) {
    return typeDefinition.persistedProperties || [];
  }
  return [];
};

export const objectHasPersistedProperty = (object, property) => {
  if (isActiveSelection(object)) {
    return object
      .getObjects()
      .every(o => objectHasPersistedProperty(o, property));
  }

  return (
    getPersistedPropertiesByType(getObjectType(object)).indexOf(property) !== -1
  );
};

export const getToggleablePersistedPropertiesByType = type => {
  const typeDefinition = PERSISTED_OBJECTS.find(def => def.type === type);
  if (typeDefinition) {
    return typeDefinition.toggleablePersistedProperties || [];
  }
  return [];
};

export const getToggledProperties = (
  defaultProperties,
  toggleableProperties,
  propertiesFlags = {},
) =>
  Object.assign(
    {},
    ...Object.entries(defaultProperties).map(pair => {
      const key = pair[0];
      const value = pair[1];
      if (toggleableProperties.includes(key)) {
        const useFlag = getToggleableFlagByProperty(key);
        return { [key]: useFlag && propertiesFlags[key] ? value : null };
      }
      return { [key]: value };
    }),
  );

const compactPropsLayout = (propsLayout, propsMapFunc = identity) =>
  // Iterate props layout and remove empty sections that shouldn't be displayed
  propsLayout
    .map(propsWrapper =>
      (propsWrapper || [])
        .map(propsGroup =>
          (propsGroup || []).map(propsMapFunc).filter(value => !isEmpty(value)),
        )
        .filter(value => !isEmpty(value)),
    )
    .filter(value => !isEmpty(value));

export const getObjectPropsLayout = (object, mode) => {
  const type = getObjectType(object);
  let propsLayout = [];
  if (type) {
    const typeDefinition = ALL_OBJECTS.find(def => def.type === type);
    if (typeDefinition) {
      if (isFunction(typeDefinition.propsLayout)) {
        propsLayout = typeDefinition.propsLayout(object, mode) || [];
      } else {
        propsLayout = typeDefinition.propsLayout || [];
      }
    }
  }
  return compactPropsLayout(propsLayout);
};

export const getObjectPropsConfig = object => {
  const type = getObjectType(object);
  if (type) {
    const typeDefinition = ALL_OBJECTS.find(def => def.type === type);
    if (typeDefinition) {
      if (isFunction(typeDefinition.propsConfig)) {
        return typeDefinition.propsConfig(object) || {};
      }
      return typeDefinition.propsConfig || {};
    }
  }
  return {};
};

export const objectCanBeAddedToSymbol = object => {
  const type = getObjectType(object);
  if (
    object.parentSymbol ||
    object.canvas?.editingSymbol ||
    object.type === 'ludusGuideline'
  ) {
    return false;
  }
  if (type) {
    const typeDefinition = ALL_OBJECTS.find(def => def.type === type);
    if (typeDefinition) {
      if (isFunction(typeDefinition.canBeAddedToSymbol)) {
        return typeDefinition.canBeAddedToSymbol(object);
      }
      if (isBoolean(typeDefinition.canBeAddedToSymbol)) {
        return typeDefinition.canBeAddedToSymbol;
      }
    }
  }
  return true;
};

export const objectHasPropCapability = (object, prop, mode = MODE_SLIDE) => {
  if (isActiveSelection(object)) {
    return object.getObjects().every(o => objectHasPropCapability(o, prop));
  }
  const props = flattenDeep(getObjectPropsLayout(object, mode));
  return includes(props, prop);
};

export const configureObject = (object, canvasSize) => {
  const type = getObjectType(object);
  if (type) {
    const typeDefinition = ALL_OBJECTS.find(def => def.type === type);
    if (typeDefinition && isFunction(typeDefinition.configFunc)) {
      typeDefinition.configFunc(object, canvasSize);
    }
  }
};

export const createObject = (type, properties) =>
  new Promise((resolve, reject) => {
    const className = getFabricClassNameForType(type);

    if (!className) {
      reject(`Unknown type "${type}"`);
      return;
    }
    const klass = fabric[className];
    if (!klass) {
      reject(`Unknown class "fabric.${className}"`);
      return;
    }
    klass.fromObject(properties, (object, error) => {
      if (error) {
        reject(error);
      }
      resolve(object);
    });
  });

export const getPropertiesForDefaultObject = (type, properties) => {
  const className = getFabricClassNameForType(type);
  if (!className) {
    return undefined;
  }
  const klass = fabric[className];
  if (!klass) {
    return undefined;
  }
  const toggleableProperties = getToggleablePersistedPropertiesByType(type);
  const defaultProperties = DEFAULT_PERSISTED_PROPERTIES[type] || {};
  const toggledDefaultProperties = getToggledProperties(
    defaultProperties,
    toggleableProperties,
  );

  return {
    ...toggledDefaultProperties,
    ...properties,
    type: klass.prototype.type,
    ludus: {
      ...toggledDefaultProperties.ludus,
      ...properties.ludus,
    },
  };
};

const getObjectDescendants = (object, limitToGroupAndSymbols = false) => {
  if (
    (limitToGroupAndSymbols && isObjectOfType(object, 'group', 'symbol')) ||
    has(object, 'objects')
  ) {
    return object.objects.reduce(
      (stack, current) =>
        stack.concat([current, ...getObjectDescendants(current)]),
      [],
    );
  }

  return [];
};

const getAllSlideObjects = slide =>
  Object.values(slide.content.objects || {}).reduce(
    (stack, object) => stack.concat([object, ...getObjectDescendants(object)]),
    [],
  );

export const getPresentationFonts = slides => {
  const fonts = [];
  const slideObjects = [];

  Object.values(slides).forEach(slide => {
    slideObjects.push(...getAllSlideObjects(slide));
  });

  slideObjects.forEach(slideObject => {
    const persistedProperties = getObjectPersistedProperties(slideObject);
    if (has(persistedProperties, 'fontFamily')) {
      fonts.push(persistedProperties.fontFamily);
    }
  });

  return uniq(fonts).sort();
};

export const getPresentationColors = slides => {
  const colors = [];
  const slideObjects = [];

  Object.values(slides).forEach(slide => {
    colors.push(utils.getNormalizedRGBAString(slide.content.background));
    slideObjects.push(...getAllSlideObjects(slide));
  });

  slideObjects.forEach(slideObject => {
    const persistedProperties = getObjectPersistedProperties(slideObject);
    // "Styles“ is specific to text objects and it's not a property
    // It's an array of multiple properties per character.
    // It must be filtered and unified to find all fill properties
    if (slideObject.styles) {
      const colorsInStyles = uniq(
        map(filter(slideObject.styles[0], 'fill'), 'fill'),
      );
      colorsInStyles.forEach(color => {
        colors.push(utils.getNormalizedRGBAString(color));
      });
    }
    if (has(persistedProperties, 'fill')) {
      if (persistedProperties.fill.colorStops) {
        // gradient
        persistedProperties.fill.colorStops.forEach(c => {
          colors.push(utils.getNormalizedRGBAString(c.color));
        });
      } else {
        colors.push(utils.getNormalizedRGBAString(persistedProperties.fill));
      }
    }
    if (has(persistedProperties, 'innerStroke')) {
      colors.push(
        utils.getNormalizedRGBAString(persistedProperties.innerStroke),
      );
    }
  });

  const colorsOccurrence = {};
  colors.forEach(color => {
    colorsOccurrence[color] = has(colorsOccurrence, color)
      ? colorsOccurrence[color] + 1
      : 1;
  }, {});

  return uniq(colors)
    .sort((a, b) => (colorsOccurrence[a] > colorsOccurrence[b] ? -1 : 1))
    .slice(0, 30)
    .map(color => ({ value: color })); // mimic library colors
};

/*
 * Class to handle a selection on canvas and compute relevant properties
 */
export class ObjectSelection {
  constructor(activeObject, mode = MODE_SLIDE) {
    this._activeObject = activeObject;
    this._mode = mode;
  }

  get activeObject() {
    return this._activeObject;
  }

  @memoizeGetter
  get name() {
    if (this.isActiveSelection) {
      return ACTIVE_SELECTION.name;
    }
    return getObjectName(this._activeObject);
  }

  @memoizeGetter
  get isActiveSelection() {
    return isActiveSelection(this._activeObject);
  }

  @memoizeGetter
  get isSymbol() {
    return isObjectOfType(this._activeObject, SYMBOL.type);
  }

  @memoizeGetter
  get canBeAddedToSymbol() {
    const containsSymbol = objectContainsType(this._activeObject, SYMBOL.type);
    const canBeAddedToSymbol = objectCanBeAddedToSymbol(this._activeObject);
    return !containsSymbol && canBeAddedToSymbol;
  }

  @memoizeGetter
  get isImage() {
    return isObjectOfType(this._activeObject, IMAGE.type);
  }

  @memoizeGetter
  get propsLayout() {
    if (this.isActiveSelection) {
      // Take props layouts for all selected objects and keep props common to all
      const allObjectsCommonProps = utils.intersection(
        this._activeObject
          .getObjects()
          .map(object => flattenDeep(getObjectPropsLayout(object, this._mode))),
      );
      // Add active selection mandatory props even if they're not common to selected objects
      const displayableProps = allObjectsCommonProps.concat(
        ACTIVE_SELECTION.mandatoryProps,
      );
      // Iterate active selection props layout and remove props that shouldn't be displayed
      return compactPropsLayout(ACTIVE_SELECTION.propsLayout, props =>
        utils.intersection([props, displayableProps]),
      );
    }
    return getObjectPropsLayout(this._activeObject, this._mode);
  }

  @memoizeGetter
  get propsConfig() {
    if (this.isActiveSelection) {
      // If this is an active selection, iterate all objects, get their props config and merge them
      return Object.assign(
        {},
        ...uniqWith(
          this._activeObject
            .getObjects()
            .map(object => getObjectPropsConfig(object)),
          isEqual,
        ),
      );
    }
    return getObjectPropsConfig(this._activeObject);
  }

  getPropConfig(prop) {
    return this.propsConfig[prop] || {};
  }

  getDefaultProperty(property, defaultProperties) {
    let value;
    if (this.isActiveSelection) {
      // If this is an active selection, iterate all objects, get their type and remove duplicates.
      const types = uniq(
        this._activeObject.getObjects().map(object => getObjectType(object)),
      );
      // Take the most frequent type
      const counter = countBy(types);
      const orderedTypes = reverse(
        sortBy(uniq(types), [type => counter[type]]),
      );
      const type = orderedTypes[0];

      value = getDefaultPropertyForType(type, property, defaultProperties);
    } else {
      value = getDefaultProperty(
        this._activeObject,
        property,
        defaultProperties,
      );
    }
    return value;
  }

  getProperty(property, defaultValue) {
    if (this.isActiveSelection) {
      // If this is an active selection, iterate all objects, get their property value
      // and remove duplicates.
      const values = this._activeObject
        .getObjects()
        .map(object =>
          new ObjectSelection(object).getProperty(property, defaultValue),
        );
      const uniqueValues = uniqWith(values, isEqual);
      // If all values are the same, take the first and only value from the array
      if (uniqueValues.length === 1) {
        return uniqueValues[0];
      }
      if (property === 'fill' || property === 'stroke') {
        return uniqueValues;
      }
      return undefined;
    }
    const value = get(this._activeObject, property); // use lodash so property paths are allowed
    if (
      this._activeObject &&
      this._activeObject instanceof fabric.LudusText &&
      [('fontWeight', 'fontStyle', 'letterSpacing', 'fill')].includes(property)
    ) {
      return this._activeObject.getSelectionStyles(false, [property])[property];
    }

    if (
      this._activeObject &&
      this._activeObject instanceof fabric.LudusText &&
      property === 'fontSize'
    ) {
      return this._activeObject.fontSize * this._activeObject.scaleX;
    }

    if (!isNil(value)) {
      return transformProperty(property, value);
    }
    if (defaultValue !== undefined && (value === undefined || value === null)) {
      return defaultValue;
    }
    return value;
  }

  setProperty(property, value) {
    if (isFunction(property)) {
      this.setProperties(property);
    } else {
      this.setProperties({ [property]: value });
    }
  }

  setProperties(properties) {
    if (this.isActiveSelection) {
      this._activeObject.forEachObject(object => {
        new ObjectSelection(object).setProperties(properties);
      });
      this._activeObject.addWithUpdate();
    } else {
      let objectProperties;
      if (isFunction(properties)) {
        objectProperties = properties(this._activeObject);
      } else {
        objectProperties = properties;
      }
      // For each passed property, use lodash so we can use property paths
      const expandedProperties = Object.keys(objectProperties).reduce(
        (result, property) => set(result, property, objectProperties[property]),
        {},
      );
      // Handle ludus special properties
      if ('ludus' in expandedProperties) {
        const newProperties = {
          // Add all passed properties
          ...expandedProperties,
          ludus: {
            // Keep previous ludus properties
            ...this._activeObject.ludus,
            // And add new ones
            ...expandedProperties.ludus,
          },
        };
        this._activeObject.set(newProperties);
      } else {
        if (this._activeObject.type === 'ludusText' && properties.fill) {
          this._activeObject.setSelectionStyles({ fill: properties.fill });
        } else {
          this._activeObject.set(objectProperties);
        }
      }
    }
    this._activeObject.setCoords();
    if (this._activeObject.canvas) {
      this._activeObject.canvas.requestRenderAll();
    }
  }

  performAction(action, payload) {
    if (!this._activeObject) {
      return;
    }
    if (this.isActiveSelection) {
      this._activeObject.forEachObject(object => {
        new ObjectSelection(object).performAction(action, payload);
      });
    } else if (isFunction(this._activeObject[action])) {
      this._activeObject[action](this._activeObject, payload);
    } else {
      console.error(`Could not execute action "${action}"`);
    }
  }

  triggerKeyDownWithKey(key) {
    if (!this._activeObject) {
      return false;
    }
    if (this.isActiveSelection) {
      return this._activeObject
        .getObjects()
        .map(object => new ObjectSelection(object).triggerKeyDownWithKey(key))
        .includes(true);
    }
    if (isFunction(this._activeObject.onKeyDownWithKey)) {
      return !!this._activeObject.onKeyDownWithKey(key);
    }
    return false;
  }

  triggerKeyUpWithKey(key) {
    if (!this._activeObject) {
      return false;
    }
    if (this.isActiveSelection) {
      return this._activeObject
        .getObjects()
        .map(object => new ObjectSelection(object).triggerKeyUpWithKey(key))
        .includes(true);
    }
    if (isFunction(this._activeObject.onKeyUpWithKey)) {
      return !!this._activeObject.onKeyUpWithKey(key);
    }
    return false;
  }
}
